import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import * as QRCode from 'qrcode';
import { buildUpiIntentUri } from '../upi.util';
import { DisplayService } from '../../services/display.service';

@Component({
    selector: 'app-upi-qr-dialog',
    standalone: true,
    imports: [CommonModule, Dialog, ButtonModule],
    template: `
        <p-dialog
            appendTo="body"
            [(visible)]="visible"
            (visibleChange)="onVisibleChange($event)"
            [modal]="true"
            [style]="{ width: 'min(24rem, 96vw)' }"
            (onShow)="onShow()"
        >
            <ng-template #header>
                <span class="font-bold">{{ heading }}</span>
            </ng-template>

            <div class="flex flex-col gap-3 text-center">
                @if (!upiId || !merchantName) {
                    <p class="text-sm text-orange-600 m-0">
                        {{ notConfiguredMessage || 'UPI payments are not configured yet.' }}
                    </p>
                } @else if (!amount || amount <= 0) {
                    <p class="text-sm text-orange-600 m-0">
                        The payment amount is not available right now. Please contact support.
                    </p>
                } @else if (qrError) {
                    <p class="text-sm text-red-500 m-0">Unable to generate the QR code. Please try again.</p>
                } @else {
                    <p class="text-lg font-semibold m-0">₹{{ amount | number: '1.2-2' }}</p>
                    @if (qrDataUrl) {
                        <img [src]="qrDataUrl" alt="UPI payment QR code" class="upi-qr-image" />
                    }
                    <p class="text-sm text-color-secondary m-0">Scan using any UPI app — Google Pay, PhonePe, Paytm, BHIM, etc.</p>
                    <div class="flex items-center justify-center gap-2 text-sm">
                        <span class="font-mono">{{ upiId }}</span>
                        <button type="button" class="upi-copy-btn" (click)="copyUpiId()" title="Copy UPI ID">
                            <i class="pi pi-copy"></i>
                        </button>
                    </div>
                    <p class="text-sm text-color-secondary m-0">{{ merchantName }}</p>
                    @if (copied) {
                        <p class="text-xs text-green-600 m-0">UPI ID copied</p>
                    }
                }
            </div>

            <ng-template #footer>
                <p-button label="Cancel" [text]="true" severity="secondary" (click)="close()" />
                @if (qrDataUrl && !qrError) {
                    <p-button label="Refresh" icon="pi pi-refresh" [text]="true" (click)="onShow()" />
                    <p-button label="Download QR" icon="pi pi-download" (click)="downloadQr()" />
                    <p-button label="Payment Completed" icon="pi pi-check" severity="success" (click)="onPaymentCompleted()" />
                }
            </ng-template>
        </p-dialog>
    `,
    styles: [`
        .upi-qr-image {
            width: 12rem;
            height: 12rem;
            margin: 0 auto;
            border: 1px solid var(--surface-300);
            border-radius: 0.5rem;
        }

        .upi-copy-btn {
            border: none;
            background: transparent;
            cursor: pointer;
            color: var(--primary-color);
        }
    `]
})
export class UpiQrDialogComponent {
    private displayService = inject(DisplayService);

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() paymentCompleted = new EventEmitter<void>();

    @Input() heading = 'Pay via QR';
    @Input() upiId: string | null | undefined;
    @Input() merchantName: string | null | undefined;
    @Input() amount: number | null | undefined;
    @Input() note: string | undefined;
    @Input() notConfiguredMessage: string | undefined;

    qrDataUrl: string | null = null;
    qrError = false;
    copied = false;

    async onShow(): Promise<void> {
        this.copied = false;
        this.qrError = false;
        this.qrDataUrl = null;

        const uri = buildUpiIntentUri({
            pa: this.upiId ?? '',
            pn: this.merchantName ?? '',
            am: this.amount ?? 0,
            tn: this.note,
        });
        if (!uri) {
            return;
        }

        try {
            this.qrDataUrl = await QRCode.toDataURL(uri, { width: 256, margin: 1 });
            // Mirror the QR to any paired customer-facing display (best-effort, non-blocking).
            void this.displayService.sendContent('payment-qr', this.amount ?? 0, this.note);
        } catch {
            this.qrError = true;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.visibleChange.emit(visible);
        if (!visible && this.qrDataUrl) {
            void this.displayService.sendContent('idle');
        }
    }

    onPaymentCompleted(): void {
        void this.displayService.sendContent('payment-success', this.amount ?? 0);
        this.qrDataUrl = null; // clears onVisibleChange's guard so close() doesn't also push 'idle'
        this.paymentCompleted.emit();
        this.close();
    }

    copyUpiId(): void {
        if (!this.upiId) {
            return;
        }
        navigator.clipboard?.writeText(this.upiId).then(() => {
            this.copied = true;
        });
    }

    downloadQr(): void {
        if (!this.qrDataUrl) {
            return;
        }
        const link = document.createElement('a');
        link.href = this.qrDataUrl;
        link.download = 'upi-payment-qr.png';
        link.click();
    }

    close(): void {
        this.visible = false;
        this.onVisibleChange(false);
    }
}
